import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	organizationDefaultMemberControllerAddMemberMutation,
	organizationDefaultMemberControllerListMembersOptions,
	organizationDefaultMemberControllerListMembersQueryKey,
	organizationDefaultMemberControllerRemoveMemberMutation,
} from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationOrganization } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import type {
	AddDefaultMemberInput,
	DefaultMemberScope,
} from '@dealstack/frontend/app/pages/Organization/OrganizationDefaultMembers/OrganizationDefaultMembers.types';

export const useOrganizationDefaultMembers = (
	organizationId: string,
	resourceType: DefaultMemberScope
) => {
	const tOrganization = useTranslationOrganization();
	const queryClient = useQueryClient();
	const path = { organizationId, resourceType };

	const listQuery = useQuery({
		...organizationDefaultMemberControllerListMembersOptions({ path }),
		enabled: !!organizationId,
	});

	const invalidate = () =>
		queryClient.invalidateQueries({
			queryKey: organizationDefaultMemberControllerListMembersQueryKey({ path }),
		});

	const addMutation = useMutation({
		...organizationDefaultMemberControllerAddMemberMutation(),
		onSuccess: async () => {
			await invalidate();
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tOrganization('programme_defaults.members.toast.add_failed'),
			});
		},
	});

	const removeMutation = useMutation({
		...organizationDefaultMemberControllerRemoveMemberMutation(),
		onSuccess: async () => {
			await invalidate();
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tOrganization('programme_defaults.members.toast.remove_failed'),
			});
		},
	});

	const addMember = (
		{ firstName, lastName, email, role }: AddDefaultMemberInput,
		params?: { onSuccess: () => void }
	) =>
		addMutation.mutate(
			{ path, body: { email, firstName, lastName, permissionRole: role } },
			{ onSuccess: () => params?.onSuccess() }
		);

	return {
		members: listQuery.data ?? [],
		isLoading: listQuery.isLoading,
		addMember,
		isAdding: addMutation.isPending,
		removeMember: (userId: string) => removeMutation.mutate({ path: { ...path, userId } }),
		isRemoving: removeMutation.isPending,
	};
};
